import React, { useEffect, useState } from "react"
import { useSpring, animated } from "react-spring"
import { useInView } from "react-intersection-observer"

const AnimatedValue = ({
  value = 0,
  duration = 800,
  className,
  simbol,
  withDecimals = false,
  withDot = true,
  shouldReanimate = false,
  initialMount = true,
  setInitialMount
}) => {
  const fastStepDuration =
    value > 13
      ? (duration - 200) / 10
      : value > 4
      ? (duration - 200) / (value - (13 - value))
      : 0
  const mediumStepDuration =
    value > 4 ? 100 : value > 2 ? duration / (value - 2) : 0
  const slowStepDuration = value > 4 ? 100 : value > 2 ? 200 : duration / value
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [firstView, setFirstView] = useState(true)
  const [ref, inView] = useInView({ threshold: 0.5, once: true })

  const props = useSpring(
    {
      from: {
        val: value - 13 > 0 ? value - 13 : value - 4 > 0 ? value - 4 : 0,
      },
      to: async (next) => {
        if (inView) {
          if (value > 4) {
            for (
              let i = value - 13 > 0 ? value - 13 : 13 - value;
              i < value - 3;
              i++
            ) {
              await next({
                val: i,
                config: { duration: fastStepDuration },
              })
            }
          }
          if (value > 2) {
            for (let i = value - 3 > 0 ? value - 3 : 1; i < value - 1; i++) {
              await next({
                val: i,
                config: { duration: mediumStepDuration },
              })
            }
          }
          if (value > 0) {
            for (let i = value - 1; i <= value; i++) {
              await next({
                val: i,
                config: { duration: slowStepDuration },
              })
            }
          }
        }
      },
      config: { duration },
    },
    [shouldAnimate, value]
  )

  useEffect(() => {

    if (firstView && inView && initialMount) {
      if (setInitialMount)
        setTimeout(() => {
          setInitialMount(false)
        }, duration)

      setFirstView(false)
      setShouldAnimate(true)
    }

    if (inView && !firstView) {
      setShouldAnimate(false)
    }
  }, [inView])

  useEffect(() => {
    let element = document.getElementById("root")

    const handleTouchStart = () => {
      props[0].val.pause()
    }

    const handleTouchEnd = () => {
      props[0].val.resume()
    }

    element.addEventListener("touchstart", handleTouchStart, { passive: true })
    element.addEventListener("touchend", handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener("touchstart", handleTouchStart, false)
      element.removeEventListener("touchend", handleTouchEnd, false)
    }
  }, [])

  return (
    <animated.div className={className} ref={ref}>
      {value != null ? (
        shouldAnimate || shouldReanimate ? (
          props[0].val?.to((val) => {
            let formattedValue = val.toLocaleString(value < 1000 ? "es-MX" : "en-US", {
              useGrouping: withDot,
              maximumFractionDigits: withDecimals ? 2 : 0,
              minimumFractionDigits: withDecimals ? 2 : 0,
            })

            if (simbol) {
              formattedValue += simbol
            }

            return formattedValue
          })
        ) : (
          value.toLocaleString(value < 1000 ? "es-MX" : "en-US", {
            style: "decimal",
            useGrouping: withDot,
            maximumFractionDigits: withDecimals ? 2 : 0,
            minimumFractionDigits: withDecimals ? 2 : 0,
          }) + (simbol || "")
        )
      ) : (
        <span className={className}>n/a</span>
      )}
    </animated.div>
  )
}

export default AnimatedValue
